import React, { ReactNode, useState } from 'react';
import { Menu, ChevronLeft, LogOut, Calendar, Settings, Users, Coffee, Baby } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleNavigation = (path: string) => {
    navigate(path);
  };
  
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div 
        className={`bg-white shadow-lg transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        } fixed h-full z-10`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <div className={`flex items-center ${!sidebarOpen && 'justify-center w-full'}`}>
            <Coffee className="h-8 w-8 text-primary-500" />
            {sidebarOpen && <span className="ml-2 text-xl font-semibold">SnackSmart</span>}
          </div>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`p-1 rounded-full hover:bg-gray-100 ${!sidebarOpen && 'hidden'}`}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        </div>
        
        <nav className="mt-6 px-2">
          <div className={`space-y-2 ${!sidebarOpen && 'flex flex-col items-center'}`}>
            <NavItem 
              icon={Calendar} 
              label="Meal Scheduler" 
              active={location.pathname === '/'} 
              collapsed={!sidebarOpen}
              onClick={() => handleNavigation('/')}
            />
            <NavItem 
              icon={Users} 
              label="Users" 
              active={location.pathname === '/users'} 
              collapsed={!sidebarOpen}
              onClick={() => handleNavigation('/users')}
            />
            <NavItem 
              icon={Baby} 
              label="Kids" 
              active={location.pathname === '/kids'} 
              collapsed={!sidebarOpen}
              onClick={() => handleNavigation('/kids')}
            />
            <NavItem 
              icon={Settings} 
              label="Settings" 
              active={location.pathname === '/settings'} 
              collapsed={!sidebarOpen}
              onClick={() => handleNavigation('/settings')}
            />
            <NavItem 
              icon={LogOut} 
              label="Logout" 
              collapsed={!sidebarOpen}
              onClick={() => console.log('Logout')}
            />
          </div>
        </nav>
      </div>
      
      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${
        sidebarOpen ? 'ml-64' : 'ml-20'
      }`}>
        <header className="bg-white shadow-sm z-10 sticky top-0">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)} 
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="ml-4 text-2xl font-semibold text-gray-800">
                {location.pathname === '/' && 'Meal Preparation Dashboard'}
                {location.pathname === '/users' && 'User Management'}
                {location.pathname === '/kids' && 'Kids Management'}
                {location.pathname === '/settings' && 'Settings'}
              </h1>
            </div>
          </div>
        </header>
        
        <main className="p-6 overflow-auto" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
          {children}
        </main>
      </div>
    </div>
  );
};

interface NavItemProps {
  icon: React.ComponentType<any>;
  label: string;
  active?: boolean;
  collapsed: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon: Icon, label, active, collapsed, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center p-3 rounded-lg ${
        active 
          ? 'bg-primary-50 text-primary-600' 
          : 'text-gray-600 hover:bg-gray-100'
      } ${collapsed ? 'justify-center' : ''}`}
    >
      <Icon className={`h-6 w-6 ${active ? 'text-primary-500' : 'text-gray-500'}`} />
      {!collapsed && <span className="ml-3 font-medium">{label}</span>}
    </button>
  );
};

export default Layout;